/**
 * Handing an attached capture from the copilot to the analyzer app.
 *
 * THE PROBLEM. The composer holds the real `File` object (see
 * `triageFiles` in ./attachments.js, which keeps it on `a.file`), but the
 * analyzer is a SEPARATE application on a separate page: /analyzer/* is served
 * by arta-analyzer through the API proxy, not by this bundle. A `File` cannot
 * be carried across a navigation in a URL, and re-asking the engineer to pick
 * the same file they just attached is the thing this feature exists to avoid.
 *
 * THE MECHANISM. Both pages are same-origin (verified: /TseDashboard5/new and
 * /analyzer/har are both served from this host), so they share IndexedDB. The
 * blob is written here under a one-time token, the token travels in the query
 * string, and the analyzer reads it back and deletes it.
 *
 * WHY IndexedDB rather than the alternatives:
 *   sessionStorage  is per-tab and string-only -- a 10MB capture would have to
 *                   be base64'd, inflating it by a third, and would not survive
 *                   opening the analyzer in a new tab.
 *   the server      already has the bytes (POST /v1/api/tse/attachments), but
 *                   `build_segment` CONSUMES an attachment id -- it is good for
 *                   exactly one message -- so the analyzer cannot fetch it back.
 *   a Blob URL      dies with the page that created it.
 *
 * IndexedDB stores a Blob natively, so nothing is re-encoded.
 *
 * DOs
 *  - Keep DB_NAME/STORE identical to arta-analyzer/src/shared/handoff.js. They
 *    are two copies in two bundles that must agree; there is no shared module
 *    because there is no shared build.
 *  - Let every failure fall back to opening the analyzer empty. A handoff that
 *    does not arrive should cost the engineer one file-picker click, never an
 *    error page.
 *
 * DON'Ts
 *  - Don't leave entries behind. Each is up to 10MB of a customer's capture
 *    sitting in browser storage; the analyzer deletes on read and this module
 *    sweeps anything stale on write.
 */

// Shared with the analyzer bundle. Changing either without the other silently
// breaks the handoff -- the analyzer would open empty and nobody would know why.
const DB_NAME = "arta-analyzer-handoff";
const STORE = "captures";
const DB_VERSION = 1;

// A handoff is consumed seconds after it is written. Anything older was
// abandoned (the engineer closed the tab, the navigation failed) and is swept.
const TTL_MS = 10 * 60 * 1000;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "token" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Drop abandoned handoffs. Best effort: a failure here must not block the new one. */
async function sweep(db) {
  try {
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const all = store.getAll();
      all.onsuccess = () => {
        const now = Date.now();
        (all.result || []).forEach((r) => {
          if (!r?.at || now - r.at > TTL_MS) store.delete(r.token);
        });
      };
      tx.oncomplete = resolve;
      tx.onerror = resolve;
      tx.onabort = resolve;
    });
  } catch {
    /* sweeping is housekeeping, never a reason to fail a handoff */
  }
}

/**
 * Which analyzer page reads this file, or null if none does.
 *
 * `.pcap` goes to /analyzer/pcap, the in-browser decoder. It needs no service,
 * uploads nothing and is instant. The sharkd-backed page at /analyzer/pcap_legacy
 * could not take a handoff anyway -- it is an iframe around webShark's own UI and
 * has no File entry point.
 */
export function analyzerRouteFor(name) {
  const ext = String(name || "").split(".").pop().toLowerCase();
  if (ext === "har") return "/analyzer/har";
  if (ext === "pcap") return "/analyzer/pcap";
  return null;
}

export const isAnalyzable = (name) => analyzerRouteFor(name) !== null;

/**
 * Stash a file for the analyzer and return the URL that opens it.
 *
 * @returns {Promise<string|null>} the analyzer URL, or null when this file has
 *   no analyzer or the browser refused to store it.
 */
export async function stageForAnalyzer(file) {
  const route = analyzerRouteFor(file?.name);
  if (!route || !file) return null;

  try {
    const db = await openDb();
    await sweep(db);
    const token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      // The Blob goes in as a Blob. IndexedDB stores it structured-cloned, so a
      // 10MB capture costs 10MB, not the ~13MB base64 would.
      tx.objectStore(STORE).put({ token, name: file.name, type: file.type || "", blob: file, at: Date.now() });
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    db.close();
    return `${route}?handoff=${encodeURIComponent(token)}`;
  } catch (err) {
    // Private browsing, a storage quota, a disabled IndexedDB. The caller opens
    // the analyzer empty instead -- one picker click, not a dead end.
    console.warn("analyzer handoff could not be staged", err);
    return null;
  }
}
